<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	import collect from 'collect.js';
	import { tick } from 'svelte';
	import Link from 'svelte-navaid/Link.svelte';
	import { navigate } from 'svelte-navaid';
	// Components
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Prompt from '@/Components/Prompt.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Partials
	import AddNote from '@/Partials/Sport/Event/_AddNote.svelte';
	import Status from '@/Partials/Sport/Event/_Status.svelte';
	import Suspend from '@/Partials/Sport/Event/_Suspend.svelte';
	import MapSportcastEvent from '@/Partials/Sport/Event/_MapSportcastEvent.svelte';
	import OverrideStartTime from '@/Partials/Sport/Event/_OverrideStartTime.svelte';
	import MarketBets from '@/Partials/Sport/Dashboard/_MarketBets.svelte';
	import MarketPricingRow from '@/Partials/Sport/Dashboard/_MarketPricingRow.svelte';
	import MarketContextMenu from '@/Partials/Sport/Dashboard/_MarketContextMenu.svelte';
	import AdjustSelectionLiability from '@/Partials/Liabilities/_AdjustSelectionLiability.svelte';
	import AdjustMarketLine from '@/Partials/Sport/Markets/_AdjustMarketLine.svelte';
	// Stores
	import MarketGroupStore from '@/Stores/Sport/MarketGroupStore.js';
	import MarketStore from '@/Stores/Sport/MarketStore.js';
	import SportSelectionStore from '@/Stores/Sport/SportSelectionStore.js';
	import SportcastFixtureStore from '@/Stores/Sport/SportcastFixtureStore.js';
	import CompetitionStore from '@/Stores/Sport/CompetitionStore.js';
	import ClassificationStore from '@/Stores/Racing/ClassificationStore.js';
	// Constants
	import config from '@/config.js';
	import { Status as MarketStatus } from '@/Constants/Market.js';
	import { SportSelectionStatus } from '@/Constants/SportSelection.js';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { convertToSlug, NumberFormat } from '@/Services/Helpers';
	import { Popover } from 'flowbite-svelte';

	dayjs.extend(localizedFormat);

	export let event;
	export let resultMarket = undefined;
	export let resultEvent = undefined;
	export let showManualResult = undefined;
	export let contextMenuOpen;
	export let showBetsForMarket;
	export let marketToShowBets;
	export let eventToShowBets;
	export let eventClassification;
	export let tabIndexValue;

	let showAddNote = false;
	let showOverrideStartTime = false;
	let showLiabilityAdjustment = false;
	let showMarketLineAdjustment = false;
	let liabilityAdjustmentMarket = undefined;
	let adjustMarketLineMarket = undefined;
	let showPromptUnresultMarket = false;
	let marketToUnresult = undefined;
	let showPromptMarketLiabilityAcceptance = false;
	let overridePrices = {};
	let liabilityCheckMarket = undefined;
	let marketLiability = {};
	let marketSelectionLiability = {};

	$: event && $CompetitionStore && $ClassificationStore && getEventClassification();
	$: marketSelectionStatus = {};
	$: marketSaving = {};
	$: marketLocking = {};
	$: marketEditingPrices = {};
	$: marketEditingManualPrices = {};
	$: marketEditingLines = {};
	$: marketParticipantSelections = {};
	$: participants = event && event.participants;
	$: participant1 = (participants && participants.firstWhere('is_home', true)) ?? '';
	$: participant2 = (participants && participants.firstWhere('is_home', false)) ?? '';
	$: keyMarkets = event && $MarketGroupStore && $MarketStore && getKeyMarkets();
	$: draw =
		keyMarkets &&
		$SportSelectionStore
			.whereIn(
				'market_id',
				keyMarkets.whereNotIn('status.id', [MarketStatus.Removed, MarketStatus.Pending]).pluck('id').toArray()
			)
			.where('name', 'Draw')
			.first();
	$: filteredKeyMarkets = keyMarkets && getFilteredKeyMarkets(keyMarkets);
	$: filteredKeyMarkets && setupMarketSelections();
	$: filteredKeyMarkets && $SportSelectionStore && overridePrices && calcTotalPercentage();
	$: eventClassification = $CompetitionStore && $ClassificationStore && getEventClassification();

	// once they start overriding prices, they have this long before it resets
	$: marketEditingPrices &&
		marketEditingPrices !== {} &&
		setTimeout(() => {
			marketEditingPrices = {};
		}, 60 * 1000);
	$: marketEditingLines &&
		marketEditingLines !== {} &&
		setTimeout(() => {
			marketEditingLines = {};
		}, 60 * 1000);

	const StatusColours = {
		[MarketStatus.Open]: 'green-500',
		[MarketStatus.Suspended]: 'orange-400',
		[MarketStatus.Closed]: 'red-600',
		[MarketStatus.Pending]: 'gray-500',
		[MarketStatus.Resulted]: 'purple-500'
	};

	function getEventClassification() {
		let competition = $CompetitionStore.firstWhere('id', event.competition.id);
		if (competition !== null && competition.classification && competition.classification.id) {
			return $ClassificationStore.firstWhere('id', competition.classification.id.toString());
		}
	}

	function getStatusLabel(market) {
		return !market.feed_status
			? `Status: ${market.status.name}`
			: market.override_status
				? `Override status: ${market.override_status.name}, Feed status: ${market.feed_status.name}`
				: `Feed status: ${market.feed_status.name}`;
	}

	function getMarketHasLockedPrices(market) {
		return !!$SportSelectionStore.where('market_id', market.id).where('override_price').count();
	}

	function getFilteredKeyMarkets(providedKeyMarkets) {
		return providedKeyMarkets.take(3).all();
	}

	function getKeyMarkets() {
		let marketIds = $MarketGroupStore
			.where('sportevent_id', event.id)
			.where('is_key', true)
			.pluck('market_ids')
			.first();

		const StatusSortOrder = {
			Open: MarketStatus.Open,
			Suspended: MarketStatus.Suspended,
			Closed: MarketStatus.Closed,
			Pending: MarketStatus.Pending,
			Resulted: MarketStatus.Resulted
		};
		const statusSortOrder = Object.values(StatusSortOrder);

		return (
			(marketIds &&
				$MarketStore
					.whereIn('id', marketIds)
					.whereNotIn('status.id', [MarketStatus.Removed])
					.sort(
						// Sort by status first THEN ID,
						(a, b) =>
							statusSortOrder.indexOf((a.override_status && a.override_status.id) ?? a.status.id) -
								statusSortOrder.indexOf((b.override_status && b.override_status.id) ?? b.status.id) ||
							marketIds.indexOf(a.id) - marketIds.indexOf(b.id) ||
							a.id - b.id
					)
					.unique((market) => market.type.id)) ?? // Will take the first of each market type found
			collect()
		);
	}

	function changeLock(market) {
		marketLocking[market.id] = true;
		if (getMarketHasLockedPrices(market)) {
			MarketStore.removeMarketOverridePrices(market.id).finally(() => {
				marketLocking[market.id] = false;
			});
		} else {
			saveOverridePrices(market);
		}
	}

	function saveOverridePrices(market) {
		if (market.status.id === MarketStatus.Resulted) return;
		if (!marketSaving[market.id] && marketLiability[market.id] < 100) {
			liabilityCheckMarket = market;
			showPromptMarketLiabilityAcceptance = true;
			return;
		}

		marketSaving[market.id] = true;
		let selectionPrices = [];
		$SportSelectionStore.where('market_id', market.id).each((marketSelection) => {
			selectionPrices.push({
				selection_id: marketSelection.id,
				override_price: (overridePrices[marketSelection.id] * 100).toFixed(0)
			});
		});
		MarketStore.updateMarketOverridePrices(market.id, selectionPrices).finally(() => {
			marketEditingPrices[market.id] = false;
			marketEditingManualPrices[market.id] = false;
			marketSaving[market.id] = false;
			marketLocking[market.id] = false;
		});
	}

	function saveOverrideLines() {
		//do stuff
	}

	function formatLiability(liability) {
		if (!liability) return '-';
		return NumberFormat(liability, true) + '%';
	}

	function calcSinglePercentage(selection) {
		if (selection.price || selection.override_price || overridePrices[selection.id]) {
			return NumberFormat(
				100 /
					(overridePrices[selection.id]
						? overridePrices[selection.id]
						: selection.override_price
							? selection.override_price / 100
							: selection.price / 100),
				true
			);
		}

		return 0;
	}

	function selectionIsSuspended(market, selection) {
		return (
			(market.status.id === MarketStatus.Suspended && !market.is_manual) ||
			!selection.tradable ||
			(selection.price === 100 && !market.is_manual) ||
			overridePrices[selection.id] === 100
		);
	}

	function setupMarketSelections() {
		if (!filteredKeyMarkets) return;
		for (const market of filteredKeyMarkets) {
			marketParticipantSelections[market.id] = {};
			$SportSelectionStore.where('market_id', market.id).each((selection) => {
				if (selection.name == 'Draw') {
					marketParticipantSelections[market.id]['Draw'] = selection.id;
				} else {
					if (!marketParticipantSelections[market.id]['Home']) {
						marketParticipantSelections[market.id]['Home'] = selection.id;
					} else if (!marketParticipantSelections[market.id]['Away']) {
						marketParticipantSelections[market.id]['Away'] = selection.id;
					}
				}
				marketSelectionStatus[selection.id] = selection?.status?.id;
				if (
					selectionIsSuspended(market, selection) ||
					(selection.override_price ? selection.override_price : selection.price) === 100
				) {
					marketSelectionStatus[selection.id] = 'SUS';
				}
			});
		}
		setupOverridePrices();
	}

	function setupOverridePrices() {
		if (!filteredKeyMarkets) return;
		overridePrices = {};
		for (const market of filteredKeyMarkets) {
			$SportSelectionStore.where('market_id', market.id).each((selection) => {
				overridePrices[selection.id] = selection.override_price
					? (selection.override_price / 100).toFixed(2)
					: (selection.price / 100).toFixed(2);
			});
		}
	}

	function calcTotalPercentage() {
		for (const market of filteredKeyMarkets) {
			marketLiability[market.id] = 0;
			$SportSelectionStore.where('market_id', market.id).each((selection) => {
				marketSelectionLiability[selection.id] = 0;
				if (
					[SportSelectionStatus.Pending, SportSelectionStatus.Active].includes(selection.status.id) &&
					!selectionIsSuspended(market, selection) &&
					((overridePrices[selection.id] &&
						overridePrices[selection.id] !== null &&
						parseFloat(overridePrices[selection.id]) * 100 > 100) ||
						(selection.override_price !== null && parseInt(selection.override_price) > 100) ||
						(!overridePrices[selection.id] && selection.override_price === null && parseInt(selection.price) > 100))
				) {
					marketSelectionLiability[selection.id] = parseFloat(calcSinglePercentage(selection));
					if (!marketLiability[market.id]) {
						marketLiability[market.id] = 0;
					}
					marketLiability[market.id] += marketSelectionLiability[selection.id];
				}
			});
		}
	}

	const getSelectionStatusClass = (selectionId) => {
		if (marketSelectionStatus[selectionId] === SportSelectionStatus.Win) {
			return 'bg-green-200';
		} else if (marketSelectionStatus[selectionId] === SportSelectionStatus.Lost) {
			return 'bg-red-200';
		} else if (marketSelectionStatus[selectionId] === SportSelectionStatus.Void) {
			return 'bg-yellow-100';
		} else if (marketSelectionStatus[selectionId] === 'SUS') {
			return 'bg-orange-100';
		}
	};

	async function showBetsForMarketInModal(market) {
		marketToShowBets = market;
		eventToShowBets = event;
		await tick();
		showBetsForMarket = true;
		window.scrollTo(0, 0);
	}

	function unresultMarket(market) {
		market
			.unresult()
			.then(() => {
				notify({
					type: 'success',
					title: market.name,
					message: 'Market successfully unresulted',
					timeout: 3
				});
				MarketStore.getMarketWithSelections(market.id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to unresult market',
					timeout: 5
				});
			});
	}

	function openOnPlatform() {
		if (contextMenuOpen == undefined) {
			navigate(`/sport/event/${event.id}?sport=${event.sport.id}&comp=${event.competition.id}`);
		}
	}

	const resetOverridePrices = async (market) => {
		marketEditingManualPrices[market.id] = false;
		marketEditingPrices[market.id] = false;
		marketSaving[market.id] = false;
		await tick();
		marketEditingManualPrices[market.id] = false;
		marketEditingPrices[market.id] = false;
		marketSaving[market.id] = false;
	};

	function acceptMarketLiabilityPrompt(market) {
		marketSaving[market.id] = true;
		saveOverridePrices(market);
		liabilityCheckMarket = undefined;
	}
</script>

{#if showPromptUnresultMarket && marketToUnresult}
	<Prompt
		bind:open={showPromptUnresultMarket}
		title="Confirm unresulting market"
		onConfirm={() => unresultMarket(marketToUnresult)}
	>
		<div class="m-3">
			<span class="text-xl">
				This will un-result all the bets already placed and cancel withdrawals, only do this when you have received
				incorrect results
			</span>
			<br />
			<br />
			<span class="font-semibold text-xl">Are you sure you want to unfinalise the market?</span>
		</div>
	</Prompt>
{/if}
{#if showBetsForMarket && eventToShowBets.id == event.id}
	<MarketBets bind:showBetsForMarket bind:marketToShowBets bind:eventToShowBets />
{/if}
{#if showOverrideStartTime}
	<OverrideStartTime {event} bind:open={showOverrideStartTime} />
{/if}
{#if showLiabilityAdjustment && liabilityAdjustmentMarket}
	<AdjustSelectionLiability bind:open={showLiabilityAdjustment} {event} market={liabilityAdjustmentMarket} />
{/if}
{#if showMarketLineAdjustment}
	<AdjustMarketLine bind:open={showMarketLineAdjustment} {event} market={adjustMarketLineMarket} />
{/if}
{#if showAddNote}
	<AddNote {event} bind:open={showAddNote} />
{/if}
{#if showPromptMarketLiabilityAcceptance}
	<Prompt
		bind:open={showPromptMarketLiabilityAcceptance}
		title="Confirm market liability acceptance"
		onConfirm={() => acceptMarketLiabilityPrompt(liabilityCheckMarket)}
		on:close={() => {
			marketLocking[liabilityCheckMarket.id] = false;
		}}
	>
		<div class="m-3">
			<span class="text-xl"> This action will make the market liability below 100%. </span>
			<br />
			<br />
			<span class="font-semibold text-xl">Are you sure you want to save these prices?</span>
		</div>
	</Prompt>
{/if}
<div class="flex flex-row items-center justify-between overflow-x-auto sm:overflow-x-visible">
	<table class="mx-1 md:mx-0 w-full border-2 text-sm hover:border-primary bg-white">
		<tr class="h-7">
			<td colspan="2" class="bg-gray-200 border-r border-black min-w-[374px]">
				<div class="flex flex-row items-center justify-between text-sm text-left">
					<div class="flex flex-row items-center space-x-2 align-middle ml-2 h-7 whitespace-nowrap">
						{dayjs(event.start_time).format('D MMM YYYY, HH:mm')}
						<button
							on:click|preventDefault|stopPropagation={() => (showOverrideStartTime = true)}
							class="ml-1 text-xs text-gray-500 hover:text-red-500 focus:outline-none"
							title="Override Start Time"
						>
							<Icon name="time" classes="w-3.5 h-3.5" />
						</button>
						<button
							on:click|preventDefault|stopPropagation={() => (showAddNote = true)}
							id={`event_notes_${event.id}`}
							class="text-xs hover:text-red-500 focus:outline-none"
							class:text-gray-500={!event.note}
							class:text-red-500={event.note}
						>
							<Icon name="edit-square" classes="w-3.5 h-3.5" />
						</button>
						<Popover
							triggeredBy={`#event_notes_${event.id}`}
							class="w-72 bg-white dark:bg-gray-800 dark:border-gray-600"
							placement="bottom-start"
						>
							<div class="p-3 space-y-2" style="white-space: pre-line">
								<h2 class="pt-1 text-lg font-bold text-center">Event Notes</h2>
								<div class="flex flex-col w-full px-3 pt-2">
									{#if event.note}
										<div class="font-medium dark:text-gray-400">
											{event.note}
										</div>
									{:else}
										<div class="font-light dark:text-gray-400">No Event Notes</div>
									{/if}
								</div>
							</div>
						</Popover>

						{#if !!event.competition.sportcast_competition_id && $SportcastFixtureStore
								.where('compId', event.competition.id)
								.count()}
							<MapSportcastEvent {event} />
						{:else if !!event.sportcast_fixture_id}
							<div class="p-1 px-1 bg-blue-200 rounded text-tiny">SGM Mapped</div>
						{/if}
					</div>
					<div class="w-full h-7" on:click={openOnPlatform} />
				</div>
			</td>
			{#if filteredKeyMarkets}
				{#each filteredKeyMarkets as market}
					<th
						class="w-[212px] min-w-[212px] max-w-[212px]"
						class:bg-gray-200={!market.is_manual}
						class:bg-orange-200={market.is_manual}
						colspan="2"
						on:click={openOnPlatform}
					>
						<div class="flex flex-row justify-start items-center py-1 gap-1.5">
							<div
								title={getStatusLabel(market)}
								class="ml-0.5 w-3 h-3 min-w-[12px] max-w-[12px] min-h-[12px] max-h-[12px] {`bg-${
									StatusColours[market.feed_status && market.feed_status.id ? market.feed_status.id : market.status.id]
								}`} rounded-full cursor-help"
							>
								{#if market.override_status && market.override_status.id}
									<div
										class="h-1.5 {`bg-${StatusColours[market.override_status.id]}`} border-b border-white rounded-t-full"
									/>
								{/if}
							</div>
							<div
								class="flex flex-row justify-between items-center w-full"
								title={market.type.name + (market.is_manual ? ' - MANUAL MARKET' : '')}
							>
								<div>{market.name}</div>
								<div class="relative">
									<button
										on:click|preventDefault|stopPropagation={() => {
											if (contextMenuOpen == event.id + '_' + market.id) {
												contextMenuOpen = undefined;
											} else {
												contextMenuOpen = event.id + '_' + market.id;
											}
										}}
										class="text-xs text-gray-500 hover:text-primary focus:outline-none w-2 market_context_menu_button"
										title="Market actions"
									>
										<Icon name="context" classes="h-4 w-4 transform-gpu scale-[1.5] -translate-x-2" />
									</button>
									{#if contextMenuOpen == event.id + '_' + market.id}
										<MarketContextMenu
											bind:event
											bind:market
											bind:showManualResult
											bind:resultMarket
											bind:resultEvent
											bind:marketToUnresult
											bind:showPromptUnresultMarket
											bind:liabilityAdjustmentMarket
											bind:showLiabilityAdjustment
											bind:contextMenuOpen
											bind:eventClassification
										/>
									{/if}
								</div>
							</div>
						</div>
					</th>
					<th
						class="py-1 w-[23px] min-w-[23px] max-w-[23px] text-center border-r border-black text-xs hover:text-primary"
						class:bg-gray-200={!market.is_manual}
						class:bg-orange-200={market.is_manual}
						on:click={openOnPlatform}
					>
						<button on:click|preventDefault|stopPropagation={() => showBetsForMarketInModal(market)}> # </button>
					</th>
				{/each}
				{#if filteredKeyMarkets.length == 1}
					<th class="py-1 w-[470px] min-w-[470px] max-w-[470px] bg-gray-200" colspan="6" on:click={openOnPlatform} />
				{:else if filteredKeyMarkets.length == 2}
					<th class="py-1 w-[235px] min-w-[235px] max-w-[235px] bg-gray-200" colspan="3" on:click={openOnPlatform} />
				{/if}
			{:else}
				<th class="py-1 w-[705px] min-w-[705px] max-w-[705px] bg-gray-200" colspan="9" on:click={openOnPlatform} />
			{/if}
		</tr>
		<tr class="h-7">
			<th class="w-[95px] min-w-[95px] max-w-[95px] py-1 text-center bg-gray-200" on:click={openOnPlatform}>Status </th>
			<th class="py-1 text-left w-full bg-gray-200 border-r border-black">
				<div class=" flex flex-row justify-between items-center">
					<Link
						href={`/sport/event/${event.id}?sport=${event.sport.id}&comp=${event.competition.id}`}
						title="Open event for editing"
						class="hover:text-green-500 w-full"
					>
						{event.name}
					</Link>
					<a
						class="flex justify-center items-center cursor-pointer hover:text-green-500 w-5"
						href="{config.SPORT_EVENT_URL}{convertToSlug(event.competition.name)}/{convertToSlug(
							event.name
						)}/{event.id}"
						rel="noreferrer"
						target="_blank"
						title="View on platform"
					>
						<Icon name="open" classes="w-5 h-5" />
					</a>
				</div>
			</th>
			{#if filteredKeyMarkets}
				{#each filteredKeyMarkets as market}
					<td
						class="w-[135px] max-w-[135px] min-w-[135px]"
						class:bg-gray-100={!market.is_manual}
						class:bg-orange-200={market.is_manual}
						title={market.type.name + (market.is_manual ? ' - MANUAL MARKET' : '')}
					>
						<div
							class="flex flex-row items-center space-x-1 w-full"
							class:justify-center={!(market.is_manual && market.rolling_type)}
						>
							{#if market.is_manual && market.rolling_type}
								<div class="w-16 flex flex-row justify-center">
									<LoadingButton
										loading={false}
										disabled={marketSaving[market.id] && marketSaving[market.id] === true}
										on:click={() => {
											adjustMarketLineMarket = market;
											showMarketLineAdjustment = true;
											contextMenuOpen = undefined;
										}}
										title="Market line value"
										className="w-10 py-1 text-xs btn btn-primary-action"
									>
										<span>{market.line}</span>
									</LoadingButton>
								</div>
							{/if}
							<div class="flex flex-row justify-center items-center">
								{#if market.is_manual}
									{#if market.status.id === MarketStatus.Open || (market.is_manual && market.status.id !== MarketStatus.Closed)}
										<button
											disabled={marketSaving[market.id] && marketSaving[market.id] === true}
											on:click={() => resetOverridePrices(market)}
											title="Reset current unsaved changes to overridden prices"
											class="py-1 text-xs bg-red-500 btn btn-black"
											class:opacity-40={marketSaving[market.id] && marketSaving[market.id] === true}
										>
											<Icon name="revert" classes="w-4 h-4" />
										</button>
										<LoadingButton
											loading={marketSaving[market.id] && marketSaving[market.id] === true}
											on:click={() => saveOverridePrices(market)}
											className="ml-1 h-6 py-1 text-xs btn btn-primary-action flex justify-center items-center"
										>
											Save
										</LoadingButton>
									{/if}
								{:else if $MarketStore && getMarketHasLockedPrices(market)}
									<LoadingButton
										disabled={marketSaving[market.id] && marketSaving[market.id] === true}
										loading={marketLocking[market.id] && marketLocking[market.id] === true}
										on:click={() => changeLock(market)}
										title="Unlock prices back to feed prices"
										class="py-1 text-xs bg-red-500 btn btn-black flex flex-row"
									>
										<Icon name="lock" classes="w-4 h-4" />
									</LoadingButton>
									<LoadingButton
										loading={marketSaving[market.id] && marketSaving[market.id] === true}
										on:click={() => saveOverridePrices(market)}
										className="ml-1 h-6 py-1 text-xs btn btn-primary-action flex justify-center items-center"
									>
										Save
									</LoadingButton>
								{:else}
									<div class="px-1">
										<LoadingButton
											disabled={marketSaving[market.id] && marketSaving[market.id] === true}
											loading={marketLocking[market.id] && marketLocking[market.id] === true}
											on:click={() => changeLock(market)}
											class="flex items-center py-1 text-xs btn btn-black"
										>
											<Icon name="unlock" classes="w-4 h-4 mr-1" />
											<span>Lock</span>
										</LoadingButton>
									</div>
								{/if}
							</div>
						</div>
					</td>
					<td
						class="text-right font-semibold text-xs"
						class:bg-gray-100={!market.is_manual}
						class:bg-orange-200={market.is_manual}
						title={market.type.name + (market.is_manual ? ' - MANUAL MARKET' : '')}
						on:click={openOnPlatform}>{formatLiability(marketLiability[market.id])}</td
					>
					<td
						class="text-center font-semibold border-r border-black text-xs"
						class:bg-gray-100={!market.is_manual}
						class:bg-orange-200={market.is_manual}
						title={market.type.name + (market.is_manual ? ' - MANUAL MARKET' : '')}
						on:click={openOnPlatform}>{market.bet_count > 0 ? market.bet_count : '-'}</td
					>
				{/each}
				{#if filteredKeyMarkets.length == 1}
					<th class="w-[470px] min-w-[470px] max-w-[470px]" colspan="6" on:click={openOnPlatform} />
				{:else if filteredKeyMarkets.length == 2}
					<th class="w-[235px] min-w-[235px] max-w-[235px]" colspan="3" on:click={openOnPlatform} />
				{/if}
			{:else}
				<th class="w-[705px] min-w-[705px] max-w-[705px]" colspan="9" on:click={openOnPlatform} />
			{/if}
		</tr>
		{#if filteredKeyMarkets}
			<!-- ROW 1 -->
			<tr class="h-7">
				<td class="w-[95px] min-w-[95px] max-w-[95px]" on:click|self={openOnPlatform}>
					<span class="flex flex-row items-center justify-center" on:click|self={openOnPlatform}>
						<Status {event} />
					</span>
				</td>
				<td class="border-r border-black" on:click={openOnPlatform}>{participant1 && participant1.name}</td>
				{#each filteredKeyMarkets as market, marketIndex (market.id)}
					<MarketPricingRow
						bind:market
						selection={$SportSelectionStore.firstWhere('id', marketParticipantSelections[market.id]['Home'])}
						bind:price={overridePrices[parseInt(marketParticipantSelections[market.id]['Home'])]}
						bind:isEditingLines={marketEditingLines[market.id]}
						bind:isEditingPrices={marketEditingPrices[market.id]}
						bind:isEditingManualPrices={marketEditingManualPrices[market.id]}
						bind:liability={marketSelectionLiability[marketParticipantSelections[market.id]['Home']]}
						tabIndexValue={tabIndexValue + marketIndex * 3}
						{saveOverrideLines}
						{saveOverridePrices}
						{getSelectionStatusClass}
					/>
				{/each}
				{#if filteredKeyMarkets.length == 1}
					<th class="w-[470px] min-w-[470px] max-w-[470px]" colspan="6" on:click={openOnPlatform} />
				{:else if filteredKeyMarkets.length == 2}
					<th class="w-[235px] min-w-[235px] max-w-[235px]" colspan="3" on:click={openOnPlatform} />
				{/if}
			</tr>
			<!-- DRAW (if it exists) -->
			{#if draw}
				<tr class="h-7">
					<td class="w-[95px] min-w-[95px] max-w-[95px]" on:click={openOnPlatform} />
					<td class="border-r border-black" on:click={openOnPlatform}>Draw</td>
					{#each filteredKeyMarkets as market, marketIndex (market.id)}
						{#if marketParticipantSelections[market.id]['Draw']}
							<MarketPricingRow
								bind:market
								selection={$SportSelectionStore.firstWhere('id', marketParticipantSelections[market.id]['Draw'])}
								bind:price={overridePrices[parseInt(marketParticipantSelections[market.id]['Draw'])]}
								bind:isEditingLines={marketEditingLines[market.id]}
								bind:isEditingPrices={marketEditingPrices[market.id]}
								bind:isEditingManualPrices={marketEditingManualPrices[market.id]}
								bind:liability={marketSelectionLiability[marketParticipantSelections[market.id]['Draw']]}
								tabIndexValue={tabIndexValue + marketIndex * 3 + 1}
								{saveOverrideLines}
								{saveOverridePrices}
								{getSelectionStatusClass}
							/>
						{:else}
							<td
								class="w-[235px] min-w-[235px] max-w-[235px] text-right border-r border-black"
								colspan="3"
								class:bg-orange-200={market.is_manual}
								on:click={openOnPlatform}
							/>
						{/if}
					{/each}
					{#if filteredKeyMarkets.length == 2}
						<th class="w-[235px] min-w-[235px] max-w-[235px]" colspan="3" on:click={openOnPlatform} />
					{/if}
				</tr>
			{/if}
			<!-- ROW 2 -->
			<tr class="h-7">
				<td class="w-[95px] min-w-[95px] max-w-[95px]">
					<div class="flex flex-row items-center justify-center">
						<Suspend {event} />
					</div>
				</td>
				<td class="border-r border-black" on:click={openOnPlatform}>{participant2 && participant2.name}</td>
				{#each filteredKeyMarkets as market, marketIndex (market.id)}
					<MarketPricingRow
						bind:market
						selection={$SportSelectionStore.firstWhere('id', marketParticipantSelections[market.id]['Away'])}
						bind:price={overridePrices[parseInt(marketParticipantSelections[market.id]['Away'])]}
						bind:isEditingLines={marketEditingLines[market.id]}
						bind:isEditingPrices={marketEditingPrices[market.id]}
						bind:isEditingManualPrices={marketEditingManualPrices[market.id]}
						bind:liability={marketSelectionLiability[marketParticipantSelections[market.id]['Away']]}
						tabIndexValue={tabIndexValue + marketIndex * 3 + 2}
						{saveOverrideLines}
						{saveOverridePrices}
						{getSelectionStatusClass}
					/>
				{/each}
				{#if filteredKeyMarkets.length == 2}
					<th class="w-[235px] min-w-[235px] max-w-[235px]" colspan="3" on:click={openOnPlatform} />
				{/if}
			</tr>
		{:else}
			<tr>
				<td>
					<Status bind:event />
				</td>
				<td on:click={openOnPlatform}>{participant1 && participant1.name}</td>
				<td colspan="12" on:click={openOnPlatform} />
			</tr>
			<tr>
				<td>
					<Suspend bind:event />
				</td>
				<td on:click={openOnPlatform}>{participant2 && participant2.name}</td>
				<td colspan="12" on:click={openOnPlatform} />
			</tr>
		{/if}
	</table>
</div>

<style>
	table {
		border: 1px solid black;
		border-collapse: collapse;
	}
</style>
